import React from "react";
import debounce from "lodash/debounce";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";

import { getBrands } from "api/requests/getBrands";
import { Brand, BrandsResponse } from "custom-types/Brand";
import useDomain from "hooks/useDomain";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCoordinates } from "redux/selectors/location";
import { getFirstQueryParam } from "utils/nextRouterUtils";

import Tab from "components/botanic/Tab";
import Tabs from "components/botanic/Tabs";
import BrandCoins from "components/brands/BrandCoins";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import PageNavigation from "components/PageNavigation";

type BrandIndexPageProps = {
  initialBrands: Array<Brand>;
  totalCount: number;
  hasAvailableNearby: boolean;
};

const PER_PAGE = 60;

enum LocationFilter {
  ALL = "all",
  NEARBY = "nearby",
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
const isLocationFilter = (location: any): location is LocationFilter =>
  Object.values(LocationFilter).includes(location);

const BrandIndexPage: NextPage<BrandIndexPageProps> = ({
  initialBrands,
  totalCount,
  hasAvailableNearby,
}) => {
  const router = useRouter();
  const domain = useDomain();
  const searchTerm = router.query.search;
  const pageCount = Math.ceil(totalCount / PER_PAGE);
  const currentPage = Number(router.query.page || 1);
  const currentLocationFilter = isLocationFilter(router.query.location)
    ? router.query.location
    : LocationFilter.NEARBY;

  const handleNearbyTabClick = () => {
    router.push({
      pathname: "/brands",
      query: {
        ...router.query,
        location: LocationFilter.NEARBY,
        page: 1,
      },
    });
  };

  const handleAllTabClick = () => {
    router.push({
      pathname: "/brands",
      query: {
        ...router.query,
        location: LocationFilter.ALL,
        page: 1,
      },
    });
  };

  const nextPage = async () => {
    router.push({
      pathname: "/brands",
      query: { ...router.query, page: currentPage + 1 },
    });
  };

  const prevPage = async () => {
    router.push({
      pathname: "/brands",
      query: { ...router.query, page: currentPage - 1 },
    });
  };

  const search = debounce((event) => {
    router.push({
      query: { ...router.query, page: 1, search: event.target.value },
    });
  }, 300);

  return (
    <>
      <Head>
        <title>Marijuana Brands | Leafly</title>
        <meta
          name="description"
          content="Explore, find, and learn more about products from the top marijuana brands."
        />
        <link rel="canonical" href={`${domain}/brands`} />
        <link
          href={`https://www.leafly.com${router.pathname}`}
          hrefLang="en-us"
          rel="alternate"
        />
        <link
          href={`https://www.leafly.ca${router.pathname}`}
          hrefLang="en-ca"
          rel="alternate"
        />
      </Head>
      <div className="bg-white">
        <Breadcrumbs>
          <Breadcrumb href="/brands" currentLocation>
            Brands
          </Breadcrumb>
        </Breadcrumbs>
        <div className="container">
          <div className="row">
            {initialBrands.length === 0 ? (
              <h1 className="p-sm">{`No results found for ${searchTerm}`}</h1>
            ) : (
              <h1 className="p-sm">
                {searchTerm
                  ? `Showing results for ${searchTerm}`
                  : "Find Brands on Leafly"}
              </h1>
            )}

            <input
              id="searchbox"
              className="p-md m-sm"
              aria-label="Brand name search"
              placeholder="Brand name search"
              type="text"
              defaultValue={searchTerm}
              onChange={search}
            />
            {hasAvailableNearby && (
              <Tabs className="m-sm mt-lg" ariaLabel="Brands page">
                <Tab
                  onClick={handleNearbyTabClick}
                  active={currentLocationFilter === LocationFilter.NEARBY}
                >
                  Available nearby
                </Tab>
                <Tab
                  onClick={handleAllTabClick}
                  active={currentLocationFilter === LocationFilter.ALL}
                >
                  All brands
                </Tab>
              </Tabs>
            )}

            <BrandCoins initialBrands={initialBrands} />
            <PageNavigation
              currentPage={currentPage}
              onNext={nextPage}
              onPrev={prevPage}
              totalPages={pageCount}
              className="pb-5"
            />
          </div>
        </div>
      </div>
    </>
  );
};

BrandIndexPage.getInitialProps = async (context) => {
  const currentState = context.store.getState();
  const countryCode = getDomainCountryCode(currentState);
  const currentPage = Number(context.query.page || 1);
  const coordinates = getCoordinates(currentState);

  const firstLocationQueryParam = getFirstQueryParam(context.query.location);
  const currentTab = isLocationFilter(firstLocationQueryParam)
    ? firstLocationQueryParam
    : LocationFilter.NEARBY;

  const searchTerm = getFirstQueryParam(context.query.search);
  const toSkip = (currentPage - 1) * PER_PAGE;

  const availableBrandsResponse = await getBrands({
    coordinates,
    countryCode,
    searchTerm,
    skip: toSkip,
    sort: "asc",
    take: PER_PAGE,
  });

  const hasAvailableNearby = Boolean(
    availableBrandsResponse.metadata.totalCount
  );

  const shouldFetchAllBrands =
    !hasAvailableNearby || currentTab === LocationFilter.ALL;
  let allBrandsResponse = {
    data: [],
    metadata: { totalCount: 0 },
  } as BrandsResponse;
  if (shouldFetchAllBrands) {
    allBrandsResponse = await getBrands({
      countryCode,
      searchTerm,
      skip: toSkip,
      sort: "asc",
      take: PER_PAGE,
    });
  }

  return {
    hasAvailableNearby,
    initialBrands:
      hasAvailableNearby && currentTab === LocationFilter.NEARBY
        ? availableBrandsResponse.data
        : allBrandsResponse.data,
    totalCount:
      hasAvailableNearby && currentTab === LocationFilter.NEARBY
        ? availableBrandsResponse.metadata.totalCount
        : allBrandsResponse.metadata.totalCount,
  };
};

export default BrandIndexPage;
